export type AuthAction = 'EDIT' | 'VIEW' | 'READ' | 'DELETE' | 'CREATE' | 'UPDATE' | 'EXPORT';

export interface IPermissionActionDescriptor {
    resource: string;
    resourceType: string;
    action: AuthAction;
    canViewAdmin?: boolean;
    description?: string;
}

export const PermissionAction = {
    CentralizedAccess: {
        AdministrationPage: {
            View: {
                resource: 'CentralizedAccess',
                resourceType: 'AdministrationPage',
                action: 'VIEW',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
            Edit: {
                resource: 'CentralizedAccess',
                resourceType: 'AdministrationPage',
                action: 'EDIT',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
            Create: {
                resource: 'CentralizedAccess',
                resourceType: 'AdministrationPage',
                action: 'CREATE',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
            Delete: {
                resource: 'CentralizedAccess',
                resourceType: 'AdministrationPage',
                action: 'DELETE',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
        },
        Role: {
            Read: {
                resource: 'Role',
                resourceType: 'Role',
                action: 'READ',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
            Create: {
                resource: 'Role',
                resourceType: 'Role',
                action: 'CREATE',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
            Update: {
                resource: 'Role',
                resourceType: 'Role',
                action: 'UPDATE',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
            Delete: {
                resource: 'Role',
                resourceType: 'Role',
                action: 'DELETE',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
        },
        RoleAssignment: {
            Read: {
                resource: 'Role Assignment',
                resourceType: 'RoleAssignment',
                action: 'READ',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
            Create: {
                resource: 'Role Assignment',
                resourceType: 'RoleAssignment',
                action: 'CREATE',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
            Update: {
                resource: 'Role Assignment',
                resourceType: 'RoleAssignment',
                action: 'UPDATE',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
            Delete: {
                resource: 'Role Assignment',
                resourceType: 'RoleAssignment',
                action: 'DELETE',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
            Export: {
                resource: 'Role Assignment',
                resourceType: 'RoleAssignment',
                action: 'EXPORT',
                canViewAdmin: true,
                description: 'Export role assignment data',
            } as IPermissionActionDescriptor,
        },
    },
    Identity: {
        User: {
            Read: {
                resource: 'Identity',
                resourceType: 'User',
                action: 'READ',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
            Create: {
                resource: 'Identity',
                resourceType: 'User',
                action: 'CREATE',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
            Update: {
                resource: 'Identity',
                resourceType: 'User',
                action: 'UPDATE',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
            Delete: {
                resource: 'Identity',
                resourceType: 'User',
                action: 'DELETE',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
        },
        Group: {
            Read: {
                resource: 'Identity',
                resourceType: 'Group',
                action: 'READ',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
            Create: {
                resource: 'Identity',
                resourceType: 'Group',
                action: 'CREATE',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
            Update: {
                resource: 'Identity',
                resourceType: 'Group',
                action: 'UPDATE',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
            Delete: {
                resource: 'Identity',
                resourceType: 'Group',
                action: 'DELETE',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
        },
        RobotAccount: {
            Read: {
                resource: 'Identity',
                resourceType: 'RobotAccount',
                action: 'READ',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
            Create: {
                resource: 'Identity',
                resourceType: 'RobotAccount',
                action: 'CREATE',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
            Update: {
                resource: 'Identity',
                resourceType: 'RobotAccount',
                action: 'UPDATE',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
            Delete: {
                resource: 'Identity',
                resourceType: 'RobotAccount',
                action: 'DELETE',
                canViewAdmin: true,
            } as IPermissionActionDescriptor,
        },
    },
} as const;

/**
 * Returns all PermissionAction descriptors under a given resource group.
 * e.g. getPermissionsForGroup('Identity') returns all User, Group, and RobotAccount permissions.
 */
export const getPermissionsForGroup = (
    groupName: keyof typeof PermissionAction,
): IPermissionActionDescriptor[] => {
    const group = PermissionAction[groupName];
    const permissions: IPermissionActionDescriptor[] = [];
    for (const type of Object.values(group)) {
        for (const action of Object.values(type)) {
            permissions.push(action);
        }
    }
    return permissions;
};

/**
 * Formats a permission descriptor into dot notation for display.
 * e.g. { resource: 'identity', resourceType: 'user', action: 'READ' } => 'Identity.User.Read'
 */
export const formatPermissionName = (descriptor: IPermissionActionDescriptor): string =>
    `${capitalize(descriptor.resource)}.${capitalize(descriptor.resourceType)}.${capitalize(descriptor.action)}`;

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();

/**
 * Returns all PermissionAction descriptors that have canViewAdmin: true.
 * Used by UserAuthPDPModelProvider to determine if a user should get admin route access.
 */
export const getAdminViewPermissions = (): IPermissionActionDescriptor[] => {
    const permissions: IPermissionActionDescriptor[] = [];
    for (const group of Object.values(PermissionAction)) {
        for (const type of Object.values(group)) {
            for (const action of Object.values(type)) {
                if (action.canViewAdmin) {
                    permissions.push(action);
                }
            }
        }
    }
    return permissions;
};
